import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { TriageData } from "../TriageWizard";
import { useLanguage } from "@/contexts/LanguageContext";

interface ChiefComplaintStepProps {
  data: TriageData;
  updateData: (data: TriageData) => void;
  prefilled?: boolean;
}

// Complaint keys for translation mapping
const COMPLAINT_KEYS = [
  { key: 'chestPain', value: 'Chest pain' },
  { key: 'shortnessOfBreath', value: 'Shortness of breath' },
  { key: 'headache', value: 'Headache' },
  { key: 'abdominalPain', value: 'Abdominal pain' },
  { key: 'fever', value: 'Fever' },
  { key: 'cough', value: 'Cough' },
  { key: 'nauseaVomiting', value: 'Nausea/Vomiting' },
  { key: 'dizziness', value: 'Dizziness' },
  { key: 'backPain', value: 'Back pain' },
  { key: 'fatigue', value: 'Fatigue' },
  { key: 'woundInjury', value: 'Wound/Injury' },
  { key: 'rashSkin', value: 'Rash/Skin issue' },
] as const;

const ChiefComplaintStep: React.FC<ChiefComplaintStepProps> = ({ data, updateData }) => {
  const { t } = useLanguage();

  const handleComplaintSelect = (complaintValue: string) => {
    updateData({
      ...data,
      chief_complaint: complaintValue
    });
  };

  const handleTextChange = (value: string) => {
    updateData({
      ...data,
      chief_complaint: value
    });
  };

  // Get translated complaint label
  const getComplaintLabel = (key: string) => {
    return t.chiefComplaint.complaints[key as keyof typeof t.chiefComplaint.complaints] || key;
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-base font-medium mb-4 block">
          {t.chiefComplaint.title}
        </Label>
        <p className="text-sm text-muted-foreground mb-4">
          {t.chiefComplaint.subtitle}
        </p>
      </div>

      {/* Quick Select Options */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">{t.chiefComplaint.commonComplaints}</Label>
        <div className="flex flex-wrap gap-2">
          {COMPLAINT_KEYS.map(({ key, value }) => (
            <Badge
              key={key}
              variant={data.chief_complaint === value ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/20 transition-colors"
              onClick={() => handleComplaintSelect(value)}
            >
              {getComplaintLabel(key)}
            </Badge>
          ))}
        </div>
      </div>

      {/* Custom Description */}
      <div className="space-y-2">
        <Label htmlFor="chief_complaint" className="text-sm font-medium">
          {t.chiefComplaint.orDescribe}
        </Label>
        <Textarea
          id="chief_complaint"
          placeholder={t.chiefComplaint.placeholder}
          value={data.chief_complaint}
          onChange={(e) => handleTextChange(e.target.value)}
          rows={4}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground">
          {t.chiefComplaint.hint}
        </p>
      </div>
    </div>
  );
};

export default ChiefComplaintStep;